import axios from "axios";
import localService from "../services/localService";
import { vm } from "../main";
import store from "../store";
import { getRedirectUrlFromRoute } from "@/router/general";
import { readableReason } from "@/helpers/humanText";

const startLoading = () => {
  vm.$store.commit("startLoading", null, { root: true });
};
const stopLoading = () => {
  vm.$store.commit("stopLoading", null, { root: true });
};
const GENERIC_ERROR_MESSAGE = "Something went wrong, please check your information and try again";

const notifyIfError = (response) => {
  if (!response) return;

  const { data, status } = response;

  if (status && (status < 200 || status >= 300)) {
    return vm.$store.dispatch(
      "showMessage",
      {
        message: readableReason(data?.message, GENERIC_ERROR_MESSAGE),
        messageType: "error",
        messageTitle: "Error",
      },
      { root: true }
    );
  }

  // Caller-driven: a refusal the caller shows next to the field it concerns is not toasted as well
  // (pass `withoutErrorHandlerFor: ["<code>"]` in the request config).
  const shownByCaller = response?.config?.withoutErrorHandlerFor;
  if (Array.isArray(shownByCaller) && data && shownByCaller.includes(data.code)) return;

  if (!data?.success && response?.request?.responseType !== "blob") {
    if (response?.config?.url == "/mshift/broker_status") {
      // API not deployed in production so temporary added condition for hide error message notification
      return;
    }
    // M-SHIFT ERRORS HAVE "ErrorCode" property
    if (data.hasOwnProperty("ErrorCode")) return;
    // Only a plain sentence reaches the person; anything with a code, path or
    // exception text in it is replaced by the fixed one.
    vm.$store.dispatch(
      "showMessage",
      { ...data, message: readableReason(data?.message, GENERIC_ERROR_MESSAGE), messageType: "error", messageTitle: "Error" },
      { root: true }
    );
  }
};

const GET_API_URL = () => {
  return process.env.VUE_APP_API_URL;
};

const options = {
  baseURL: GET_API_URL(),
  headers: {
    "Content-Type": "application/json",
  },
};

const instance = axios.create({ ...options });

// Create an AbortController for each request
// const createAbortController = () => {
//   const abortController = new AbortController();
//   return abortController;
// };

instance.interceptors.request.use((config) => {
  // send withoutLoading = true in config of the api call if you wish to have a different api loader
  // otherwise by default it will be treated as false and loading will be shown

  !config.withoutLoading && startLoading();

  if (localStorage.getItem("userInfo")) {
    const token = localService.getItem("userInfo")["access_token"];
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // if in the store state 'salesperson/user' is set, append, the 'salesperson/user' organization_id to the config.url
    if (store.state.salesperson.organization) {
      config.headers.organizationId = store.state.salesperson.organization.entity_id;
    }
  }

  // Add an AbortController instance to the request config
  // config.abortController = createAbortController();

  return config;
});

instance.interceptors.response.use(
  (response) => {
    stopLoading();
    // A caller that handles the answer itself opts out of the toast
    if (!response?.config?.withoutErrorHandler) notifyIfError(response);
    return response;
  },
  async (error) => {
    stopLoading();
    if (error.response?.status === 401) {
      localService.clear();
      let redirectPath = await getRedirectUrlFromRoute(vm.$route);
      if (redirectPath) {
        redirectPath = `?redirect=${redirectPath}`;
      }
      if (vm.$route.path !== `/login`) {
        vm.$router.push({ path: `/login${redirectPath}` });
      }
    } else if (error.config?.withoutErrorHandler) {
      // The caller handles the failure itself
    } else if (!(error.response?.status === 400 && error.config?.url?.includes?.("quotes/refresh"))) {
      // Don't show error toast for quotes/refresh when there's no submission (400)
      notifyIfError(error.response);
    }
    throw error;
  }
);

export default instance;
